import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  lessonApi,
  type GenerateSectionPodcastResponse,
  type LessonPodcast,
  type SectionPodcastsResponse,
  type SectionPodcastSummary,
} from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import { track } from '@/config/observability';
import { notify } from '@/lib/notify';
import {
  useOptionalPodcastPlayer,
  type PodcastTrack,
} from '@/contexts/PodcastPlayerContext';

/**
 * Section podcasts - one two-host podcast per lesson section, made on demand
 * (backend `lesson/podcasts.py`). Free makes the first section's only; each
 * one costs a unit of the podcast quota.
 */

const IN_FLIGHT = new Set(['pending', 'processing']);

export function isPodcastInFlight(status: string | undefined): boolean {
  return !!status && IN_FLIGHT.has(status);
}

/** Every section of the lesson with its podcast's state. Polls while one is
 *  being made, and while the lesson is still being planned (no sections). */
export function useSectionPodcasts(lessonId: string | undefined) {
  return useQuery<SectionPodcastsResponse>({
    queryKey: queryKeys.lessons.podcasts(lessonId ?? ''),
    queryFn: () => lessonApi.getSectionPodcasts(lessonId as string),
    enabled: !!lessonId,
    staleTime: 15 * 1000,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return false;
      if (data.sections.length === 0) return 10_000;
      return data.sections.some(s => isPodcastInFlight(s.status))
        ? 5000
        : false;
    },
  });
}

/** One section from the list (shares the list's cache and polling). */
export function useSectionPodcastSummary(
  lessonId: string | undefined,
  sectionIndex: number | null
): SectionPodcastSummary | undefined {
  const { data } = useSectionPodcasts(lessonId);
  return data?.sections.find(s => s.index === sectionIndex);
}

/** One section's podcast: audio URL, peaks and transcript. */
export function useSectionPodcast(
  lessonId: string | undefined,
  sectionIndex: number | null
) {
  return useQuery<LessonPodcast>({
    queryKey: queryKeys.lessons.podcast(lessonId ?? '', sectionIndex ?? 0),
    queryFn: () =>
      lessonApi.getSectionPodcast(lessonId as string, sectionIndex as number),
    enabled: !!lessonId && sectionIndex !== null,
    staleTime: 30 * 1000,
    refetchInterval: query =>
      isPodcastInFlight(query.state.data?.status) ? 5000 : false,
  });
}

/** Make one section's podcast. A 402 (cap reached, or a later section on
 *  Free) opens the app-wide limit dialog from the API client. */
export function useGenerateSectionPodcast(lessonId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<GenerateSectionPodcastResponse, Error, number>({
    mutationFn: sectionIndex =>
      lessonApi.generateSectionPodcast(lessonId as string, sectionIndex),
    // Never re-send: a timed-out POST may already have queued the podcast.
    retry: false,
    onMutate: sectionIndex => {
      // Show it as being made straight away, not after the next poll.
      queryClient.setQueryData<SectionPodcastsResponse>(
        queryKeys.lessons.podcasts(lessonId ?? ''),
        prev =>
          prev && {
            ...prev,
            sections: prev.sections.map(s =>
              s.index === sectionIndex
                ? { ...s, status: 'pending', error: null }
                : s
            ),
          }
      );
    },
    onSuccess: (_data, sectionIndex) => {
      if (lessonId) {
        track('podcast_generated', {
          lesson_id: lessonId,
          section_index: sectionIndex,
        });
      }
    },
    onError: err => {
      if ((err as { status?: number }).status !== 402) notify.error(err);
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.podcasts(lessonId ?? ''),
      }),
  });
}

/** The lesson's title, from the metadata query the Text tab also reads. */
export function useLessonTitle(lessonId: string | undefined): string {
  return (
    useQuery({
      queryKey: queryKeys.lessons.metadata(lessonId ?? ''),
      queryFn: () => lessonApi.getLesson(lessonId as string),
      enabled: !!lessonId,
      staleTime: 30 * 1000,
    }).data?.title || 'Lesson'
  );
}

export function toTrack(
  lessonId: string,
  lessonTitle: string,
  podcast: LessonPodcast
): PodcastTrack | null {
  if (!podcast.audio_url) return null;
  return {
    lessonId,
    lessonTitle,
    sectionIndex: podcast.section_index,
    sectionTitle: podcast.section_title || `Section ${podcast.section_index}`,
    audioUrl: podcast.audio_url,
    duration: podcast.duration,
    peaks: podcast.peaks ?? [],
  };
}

/** Play a section's made podcast in the app's player - or pause / resume
 *  it, if it is the one already loaded. `loading` is the section whose
 *  audio is being fetched. */
export function useListenToSection(lessonId: string | undefined) {
  const queryClient = useQueryClient();
  const player = useOptionalPodcastPlayer();
  const lessonTitle = useLessonTitle(lessonId);
  const [loading, setLoading] = useState<number | null>(null);

  const listen = async (sectionIndex: number) => {
    if (!player || !lessonId) return;
    if (player.isCurrent(lessonId, sectionIndex)) {
      player.toggle();
      return;
    }
    setLoading(sectionIndex);
    try {
      const podcast = await queryClient.fetchQuery({
        queryKey: queryKeys.lessons.podcast(lessonId, sectionIndex),
        queryFn: () => lessonApi.getSectionPodcast(lessonId, sectionIndex),
        staleTime: 30 * 1000,
      });
      const track = toTrack(lessonId, lessonTitle, podcast);
      if (!track) throw new Error("The podcast's audio didn't load.");
      player.play(track);
    } catch (err) {
      notify.error(err, "Couldn't play the podcast");
    } finally {
      setLoading(null);
    }
  };

  return { listen, loading, player };
}
